<template>
  <div class="container-fluid py-4">
    <!-- Header -->
    <div class="row mb-4">
      <div class="col-12 d-flex justify-content-between align-items-center">
        <h4 class="mb-0">{{ $t("employees.listTitle") }}</h4>
        <router-link to="/add-employee">
          <button class="btn text-white add-btn">
            {{ $t("employees.addEmployee") }}
          </button>
        </router-link>
      </div>
    </div>

    <!-- Search + Export -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card-header p-3 d-flex justify-content-between align-items-center flex-wrap gap-2 header-bar">
          <h6 class="text-white m-0">{{ $t("employees.tableTitle") }}</h6>
          <div class="d-flex align-items-center gap-2">
            <div class="search-container">
              <input
                v-model="searchTerm"
                type="text"
                class="form-control border-0 shadow-none"
                :placeholder="$t('employees.searchPlaceholder')"
              />
              <i class="fas fa-search text-secondary"></i>
            </div>
            <button
              @click="exportToExcel(filteredUsers, 'Employees_Export')"
              class="btn btn-sm export-btn"
            >
              <i class="fas fa-file-export me-1"></i>
              {{ $t("employees.export") }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Employee Cards Grid -->
    <div v-if="!loading" class="row g-4">
      <div
        class="col-xl-3 col-lg-4 col-md-6"
        v-for="user in paginatedUsers"
:key="user.id"

      >
        <div class="card employee-card h-100">
          <div class="card-body d-flex flex-column align-items-center text-center">

            <div class="profile-img-wrapper">
              <img
  :src="buildPhotoUrl(user.photo) || placeholder"
  @error="handleImageError"
  class="profile-img"
  alt="User Avatar"
/>

            </div>
            <h5 class="mt-3 mb-1">{{ user.firstname }} {{ user.lastname }}</h5>
            <span class="text-muted small">{{ user.code || 'N/A' }}</span>
            <div class="info mt-3 w-100">
              <p>{{ user.company_name || 'N/A' }}</p>
              <p>{{ user.project_name || 'N/A' }}</p>
            </div>
            <div class="mt-auto d-flex gap-2">
              <!-- Material Manager sees Materials button -->
              <button
                v-if="isMaterialManager"
                @click="goToMaterials(user.id)"
                class="btn btn-danger btn-sm"
              >
                <i class="fas fa-box me-1"></i>
                {{ $t("employees.materials") }}
              </button>
              
              <!-- Admin/SuperAdmin see DOCS and EDIT buttons -->
              <template v-else>
                <button
                  @click="goToDocuments(user.id)"
                  class="btn btn-outline-danger btn-sm"
                >
                  <i class="fas fa-file-alt me-1"></i>
                  {{ $t("employees.docs") }}
                </button>
                <button
                  @click="goToEdit(user.id)"
                  class="btn btn-danger btn-sm"
                >
                  <i class="fas fa-pen me-1"></i>
                  {{ $t("employees.edit") }}
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border text-danger" role="status">
        <span class="visually-hidden">{{ $t("employees.loading") }}</span>
      </div>
    </div>

    <!-- No Results -->
    <div
      v-if="!loading && Array.isArray(filteredUsers) && filteredUsers.length === 0"
      class="text-center text-muted my-5"
    >
      {{ $t("employees.noResults") }}
      <div class="mt-3">
        <button
          @click="searchTerm = ''"
          class="btn btn-outline-secondary btn-sm"
        >
          {{ $t("employees.clearSearch") }}
        </button>
      </div>
    </div>

    <!-- Pagination -->
    <div class="d-flex justify-content-center mt-4" v-if="totalPages > 1">
      <nav>
        <ul class="pagination pagination-rounded mb-0">
          <li class="page-item" :class="{ disabled: currentPage === 1 }">
            <button class="page-link" @click="changePage(currentPage - 1)">
              {{ $t("employees.previous") }}
            </button>
          </li>
          <li
            class="page-item"
            v-for="page in totalPages"
            :key="page"
            :class="{ active: page === currentPage }"
          >
            <button class="page-link" @click="changePage(page)">{{ page }}</button>
          </li>
          <li
            class="page-item"
            :class="{ disabled: currentPage === totalPages }"
          >
            <button class="page-link" @click="changePage(currentPage + 1)">
              {{ $t("employees.next") }}
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/api'
import placeholderImg from '@/assets/img/placeholder.png'
import debounce from 'lodash.debounce'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'

// ⚙️ URL de base de l'API (définie dans ton .env : VUE_APP_API_URL)
const RAW_BASE = (process.env.VUE_APP_API_URL || '').replace(/\/+$/, '')
const FILE_BASE = RAW_BASE.replace(/\/api$/, '')

// Normalise l'URL de la photo renvoyée par l'API
const buildPhotoUrl = (photo) => {
  if (!photo) return null
  let url = String(photo).trim()

  // Si l'API renvoie déjà une URL complète
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
      .replace('http://localhost:8000', FILE_BASE)
      .replace('https://localhost:8000', FILE_BASE)
  }

  // Chemin relatif
  url = url.replace(/^\/+/, '')
  url = url.replace(/^storage\//, '')

  return `${FILE_BASE}/storage/${url}`
}

const router = useRouter()
const placeholder = placeholderImg

// ✅ Données
const allUsers = ref([])       // tous les employés (toutes pages)
const filteredUsers = ref([])  // résultat après recherche
const searchTerm = ref('')
const loading = ref(true)

// ✅ Pagination FRONT
const currentPage = ref(1)
const perPage = 8

const totalPages = computed(() => {
  const n = Math.ceil(filteredUsers.value.length / perPage)
  return n > 0 ? n : 1
})

const paginatedUsers = computed(() => {
  const start = (currentPage.value - 1) * perPage
  return filteredUsers.value.slice(start, start + perPage)
})

// ✅ Role detection
const isMaterialManager = computed(() => {
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  return user.roles && user.roles.includes('Material Manager')
})

// ✅ Charger TOUS les employés (en parcourant toutes les pages backend)
// (pas de search backend ici)
const fetchAllUsers = async () => {
  try {
    loading.value = true
    allUsers.value = []
    filteredUsers.value = []
    currentPage.value = 1

    let page = 1
    let lastPage = 1
    const batchSize = 100 // augmente si tu veux (200/300) selon ton API

    do {
      const res = await axios.get('/getAllUser', {
        params: { page, per_page: batchSize }
      })

      const data = Array.isArray(res.data?.data) ? res.data.data : []
      allUsers.value.push(...data)

      lastPage = res.data?.last_page || 1
      page++
    } while (page <= lastPage)

    // après chargement, applique un filtre vide (affiche tout)
    applyFilter()
  } catch (err) {
    console.error('Fetch error:', err)
  } finally {
    loading.value = false
  }
}

// ✅ Recherche FRONT sur allUsers
const applyFilter = debounce(() => {
  const term = searchTerm.value.trim().toLowerCase()

  const filtered = allUsers.value.filter((user) => {
    const fullName = `${user.firstname || ''} ${user.lastname || ''}`.toLowerCase()
    const code = String(user.code || '').toLowerCase()
    const project = String(user.project_name || '').toLowerCase()
    const company = String(user.company_name || '').toLowerCase()

    return (
      fullName.includes(term) ||
      code.includes(term) ||
      project.includes(term) ||
      company.includes(term)
    )
  })

  filteredUsers.value = filtered.sort((a, b) => {
    if (a.status === 'Active' && b.status !== 'Active') return -1
    if (b.status === 'Active' && a.status !== 'Active') return 1
    return (a.firstname || '').localeCompare(b.firstname || '')
  })
}, 300)

// ✅ Quand on tape : on reset à la page 1 puis on filtre
watch(searchTerm, () => {
  currentPage.value = 1
  applyFilter()
})

// ✅ Pagination
const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page
  }
}

const handleImageError = (event) => {
  event.target.src = placeholder
}

const goToEdit = (userId) => {
  router.push({ path: '/edit-employee', query: { user_id: userId } })
}

const goToDocuments = (userId) => {
  router.push({ path: '/employee/information/documents', query: { user_id: userId } })
}

// ✅ Export Excel (exporte TOUS les résultats filtrés)
const exportToExcel = async (dataToExport, fileName) => {
  // ici dataToExport sera filteredUsers (résultats globaux)
  const arr = Array.isArray(dataToExport?.value) ? dataToExport.value : dataToExport

  if (!Array.isArray(arr) || arr.length === 0) {
    alert('No data to export.')
    return
  }

  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet('Employees')

  worksheet.columns = [
    { header: 'First Name', key: 'firstname', width: 20 },
    { header: 'Last Name', key: 'lastname', width: 20 },
    { header: 'Code', key: 'code', width: 15 },
    { header: 'Company', key: 'company_name', width: 25 },
    { header: 'Project', key: 'project_name', width: 25 }
  ]

  arr.forEach(user => {
    worksheet.addRow({
      firstname: user.firstname || '',
      lastname: user.lastname || '',
      code: user.code || 'N/A',
      company_name: user.company_name || 'N/A',
      project_name: user.project_name || 'N/A'
    })
  })

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })

  saveAs(blob, `${fileName}.xlsx`)
}

onMounted(() => {
  fetchAllUsers()
})


</script>




<style scoped>
.add-btn {
  background-color: #4b5563;
  border-radius: 8px;
  padding: 6px 16px;
  font-weight: 500;
}
/*barre*/
.header-bar {
  background: linear-gradient(195deg, #4b5563, #8d2e38);
  border-radius: 12px;
}


.search-container {
  height: 40px;
  width: 280px;
  background-color: white;
  border-radius: 8px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.export-btn {
  background-color: white;
  color: #b03a45;
  border: 1px solid #b03a45;
  font-weight: 500;
  transition: all 0.2s ease;
}

.export-btn:hover {
  background-color: #b03a45;
  color: white;
}

.employee-card {
  border-radius: 16px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.1);
  padding: 12px;
}

.employee-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.15);
}

.profile-img-wrapper {
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #b03a45;
}

.profile-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-body h5 {
  font-size: 18px;
  font-weight: 600;
}

.card-body p {
  font-size: 14px;
  margin-bottom: 0.5rem;
}

.btn-danger,
.btn-primary {
  background-color: #4b5563 !important;
  border-color: #b03a45 !important;
  color: #fff !important;
}

.btn-danger:hover,
.btn-primary:hover {
  background-color: #8d2e38 !important;
  border-color: #8d2e38 !important;
}

.btn-outline-danger {
  color: #b03a45 !important;
  border-color: #b03a45 !important;
  background-color: transparent !important;
}

.btn-outline-danger:hover {
  background-color: #b03a45 !important;
  border-color: #b03a45 !important;
  color: #fff !important;
}

.text-danger {
  color: #b03a45 !important;
}

.spinner-border.text-danger {
  color: #b03a45 !important;
}

/* Pagination controls styling */
.pagination .page-link {
  color: #8d2e38 !important;
  border-color: #8d2e38 !important;
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 12px;
  font-weight: 500;
  min-width: 40px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
}

.pagination .page-link:hover {
  background-color: #8d2e38 !important;
  color: white !important;
}

.pagination .page-item.active .page-link {
  background-color: #8d2e38 !important;
  border-color: #8d2e38 !important;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d !important;
  border-color: #dee2e6 !important;
}

/* Special styling for Previous and Next buttons */
.pagination .page-item:first-child .page-link,
.pagination .page-item:last-child .page-link {
  width: auto;
  min-width: 80px;
  padding: 0 12px;
  font-size: 11px;
  border-radius: 20px;
}
</style>
